<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-stepper :steps="rmSteps" :current="currentStep" />
        <hm-spinner v-if="!ready" />
        <Loading :active="busy" :is-full-page="false">
          <slot>
            <hm-spinner />
          </slot>
        </Loading>
        <hm-alert />

        <b-form @submit.prevent="onSubmit" v-if="ready">
          <h4>Step 3: Determine Risk Level</h4>
          <hr />

          <h6 class="pb-3">
            Threat:
            <strong>{{threat.threat}}</strong>
            <hm-popover v-if="threat.threatInfo" :title="threat.threat" :content="threat.threatInfo" />
          </h6>

          <h6 class="pb-3">
            Vulnerability:
            <strong>{{threat.vuln}}</strong>
            <hm-popover v-if="threat.vulnInfo" :title="threat.vuln" :content="threat.vulnInfo" />
          </h6>
          <hr />

          <hm-note><u>Note</u>: Several safeguards are marked implemented by default. We do this because it’s assumed you will thoroughly and accurately complete all areas within the HIPAAMATE application in a timely manner, and will respond appropriately to alerts, notifications, and communications.</hm-note>

          <div v-if="safeguardsPos.length > 0" class="pb-3">
            Safeguards currently implemented, which reduce the likelihood that the threat will trigger/exploit the vulnerability or reduce the potential impact to your business should the vulnerability be triggered/exploited:
            <div v-for="(item, index) in safeguardsPos" :key="index" class="pt-2">
              <div class="pl-3">
                <i class="icon-check text-success"></i>
                {{item.title}}
                <hm-popover v-if="item.desc || item.list" :title="item.title" :content="item.desc" :content-list="item.list" />
              </div>
            </div>
          </div>

          <div v-if="safeguardsNeg.length > 0" class="pb-3">
            Safeguards NOT implemented, which increase the likelihood the threat will trigger/exploit the vulnerability or increase the potential impact to your business should the vulnerability be triggered/exploited:
            <div v-for="(item, index) in safeguardsNeg" :key="index" class="pt-2">
              <div class="pl-3">
                <i class="icon-exclamation text-danger"></i>
                {{item.title}}
                <hm-popover v-if="item.desc || item.list" :title="item.title" :content="item.desc" :content-list="item.list" />
              </div>
            </div>
          </div>

          <div class="pt-3">
            <strong>Taking into consideration the safeguards you currently have implemented to protect ePHI, what is the likelihood (probability) the threat will exploit the vulnerability?</strong>
          </div>
          <div class="text-danger">{{chanceLabel}}: {{chanceDetails}}</div>
          <div class="py-5 mx-4">
            <vue-slider ref="sliderChance" :min="1" :max="5" :marks="true" tooltip="always" v-model="rathreat.chance" :show="ready" :disabled="busy">
              <template v-slot:tooltip="{ value }">
                <hm-popover :label="chanceLabel" label-class="small" placement="top">{{chanceDetails}}</hm-popover>
              </template>
            </vue-slider>
          </div>

          <div class="pt-3">
            <strong>Let’s assume for a moment this threat actually does exploit the vulnerability and ePHI is compromised. If this were to occur, what would the potential impact be to your practice?</strong>
            <hm-popover title="Potential Outcomes">
              <div>
                If a threat triggers or exploits a specific vulnerability, there are many potential outcomes. The most common outcomes include:
                <ul>
                  <li>Unauthorized access to or disclosure of ePHI</li>
                  <li>Permanent loss or corruption of ePHI</li>
                  <li>Temporary loss of ePHI</li>
                  <li>Loss of financial cash flow</li>
                  <li>Loss of physical assets</li>
                  <li>Damage to business reputation</li>
                  <li>Staff time and energy addressing breach</li>
                </ul>
              </div>
            </hm-popover>
          </div>
          <div>
            <div class="text-danger">{{impactLabel}}: {{impactDetails}}</div>
            <div class="py-5 mx-4">
              <vue-slider ref="sliderImpact" :min="1" :max="5" :marks="true" tooltip="always" v-model="rathreat.impact" :show="ready" :disabled="busy">
                <template v-slot:tooltip="{ value }">
                  <hm-popover :label="impactLabel" label-class="small" placement="top">{{impactDetails}}</hm-popover>
                </template>
              </vue-slider>
            </div>
          </div>

          <div class="pt-3 pb-3">
            <h4>Risk Level: {{riskLevel}}</h4>
          </div>

          <hr />
          <b-row>
            <b-col cols="6">
              <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/risk/level">Back</b-button>
            </b-col>
            <b-col cols="6" class="text-right">
              <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Continue</b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { riskService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'RiskAnalysisLevel',
  props: ['id'],
  data() {
    return {
      rm: {
        threats: []
      },
      threat: {},
      safeguards: [],
      ready: false,
      busy: false,
      submitted: false,
      currentStep: 3
    }
  },
  computed: {
    ...mapState(['rmSteps']),
    ...mapState('maps', ['inventoryMap', 'riskMap', 'safeguardsMap', 'threatMap']),
    rathreat() {
      for (let i = 0; i < this.rm.threats.length; i++) {
        if (this.rm.threats[i].id === this.id) {
          if (!this.rm.threats[i].chance) {
            this.$set(this.rm.threats[i], 'chance', 1); // must use Vue.set to get things to be reactive to the change
          }
          if (!this.rm.threats[i].impact) {
            this.$set(this.rm.threats[i], 'impact', 1); // must use Vue.set to get things to be reactive to the change
          }

          return this.rm.threats[i];
        }
      }
      console.log("WARNING: RA Threat not found!!", this.id);
      return { chance: 1, impact: 1 };
    },
    chanceLabel() {
      return this.rathreat.chance ? this.riskMap.Chance[this.rathreat.chance].name : '';
    },
    chanceDetails() {
      return this.rathreat.chance ? this.riskMap.Chance[this.rathreat.chance].value : '';
    },
    impactLabel() {
      return this.rathreat.impact ? this.riskMap.Impact[this.rathreat.impact].name : '';
    },
    impactDetails() {
      return this.rathreat.impact ? this.riskMap.Impact[this.rathreat.impact].value : '';
    },
    riskLevel() {
      return this.rathreat.chance * this.rathreat.impact;
    },
    safeguardsPos() {
      if (this.rm.seed) {
        let seed = this.rm.seed; // needed for eval
        // build list of pos test safeguards
        return Enumerable.from(this.safeguards)
          .where(x => {
            let hasLoop = x.hasOwnProperty('loop');
            return (x.posList && x.posList.length > 0) || // loop with a pos list
              (!hasLoop && x.pos && eval(x.pos)); // no loop with a pos evaluation
          })
          .select(x => { return { title: x.title, desc: x.desc, list: x.posList }; })
          .toArray();
      } else {
        return [];
      }
    },
    safeguardsNeg() {
      if (this.rm.seed) {
        let seed = this.rm.seed; // needed for eval
        // build list of neg test safeguards
        return Enumerable.from(this.safeguards)
          .where(x => {
            let hasLoop = x.hasOwnProperty('loop');
            return (x.negList && x.negList.length > 0) || // loop with a neg list
              (!hasLoop && x.neg && eval(x.neg)); // no loop with a neg evaluation
          })
          .select(x => { return { title: x.title, desc: x.desc, list: x.negList }; })
          .toArray();
      } else {
        return [];
      }
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertError: 'alert/error',
      alertException: 'alert/exception'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            this.rathreat.approved = true;

            return riskService.setRiskManagement(this.rm)
              .then(data => {
                this.$router.push('/risk/level');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving risk management.' });
              });
          } else {
            this.alertError('Please complete threat information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    updateSafeguards(safeguards) {
      this.safeguards = safeguards;

      // expand safeguards that have items to loop over
      for (let i = 0; i < this.safeguards.length; i++) {
        if (this.safeguards[i].hasOwnProperty('loop')) {
          let seed = this.rm.seed; // needed for eval
          console.log('RAEval[Loop]:', safeguards[i].loop);
          let loopEval = eval(safeguards[i].loop);
          for (let obj in loopEval) {
            let item = loopEval[obj]; // needed for eval
            let InventoryMap = this.inventoryMap; // needed for eval

            // test loop item for positive safeguard
            console.log('RAEval[Pos]:', safeguards[i].pos);
            if (eval(this.safeguards[i].pos)) {
              if (!this.safeguards[i].posList) {
                this.safeguards[i].posList = [];
              }

              console.log('RAEval[Pos List]:', safeguards[i].list);
              this.safeguards[i].posList = this.safeguards[i].posList.concat(eval(this.safeguards[i].list));
            }

            // test loop item for negative safeguard
            console.log('RAEval[Neg]:', safeguards[i].neg);
            if (eval(this.safeguards[i].neg)) {
              if (!this.safeguards[i].negList) {
                this.safeguards[i].negList = [];
              }

              console.log('RAEval[Neg List]:', safeguards[i].list);
              this.safeguards[i].negList = this.safeguards[i].negList.concat(eval(this.safeguards[i].list));
            }
          }
        }
      }
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchInventoryMap'),
        this.$store.dispatch('maps/fetchRiskMap'),
        this.$store.dispatch('maps/fetchSafeguardsMap'),
        this.$store.dispatch('maps/fetchThreatMap'),
        riskService.getCurrentRiskManagement()
      ]).then((values) => {
        let rm = values[4];

        // ensure that all threats have a chance and impact value (needed for reactivity in vue)
        for (let i = 0; i < rm && rm.threats && rm.threats.length; i++) {
          if (!rm.threats[i].chance) rm.threats[i].chance = 1;
          if (!rm.threats[i].impact) rm.threats[i].impact = 1;
        }

        this.rm = rm;

        this.threat = Enumerable.from(this.threatMap)
          .join(this.rm.threats,
            (o) => { return o.id + ':' + o.revision; },
            (i) => { return i.id + ':' + i.revision; },
            (o, i) => { return { id: o.id, threat: o.threat, threatInfo: o.threatInfo, vuln: o.vuln, vulnInfo: o.vulnInfo, active: i.active, safeguards: o.safeguards }; })
          .where(x => { return x.id == this.id; })
          .firstOrDefault();

        this.updateSafeguards(
          Enumerable.from(JSON.parse(JSON.stringify(this.safeguardsMap)))
            .where(x => { return this.threat.safeguards.indexOf(x.id) > -1; })
            .toArray()
        );
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading risk management data.' });
      }).finally(() => {
        this.ready = true;
      })
    }

  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>